// src/pages/Leaderboards/index.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Table, Input, Button, Skeleton, Result } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { Helmet } from 'react-helmet-async';
import Header from '../Home/LandingPageHeader/Header';

import leftLayer from '../../assets/img/leftLayer.png';
import rightLayer from '../../assets/img/rightLayer.png';
import avalancheLogo from '../../assets/sponsors/avalancheLogo.png';

// If you add the nginx proxy (recommended), switch ENDPOINT as shown below.
// const ENDPOINT = '/lb-api/scores/get/summary';
const ENDPOINT = 'https://tournaments.undeadblocks.com/api/rest/scores/get/summary';

/** ----- Layout copied/adapted from UnderConstruction (dark background, top padding, etc.) ----- */
const Wrapper = styled.div`
  background: #000000;
  width: 100%;
  min-height: 100vh;
  padding: 126rem 0 80rem 0; /* keep same top offset as UnderConstruction */
  position: relative;
  overflow: hidden;

  @media screen and (max-width: 767px) {
    padding: 350rem 0 64rem 0;
  }
`;

const Info = styled.div`
  margin: 0rem 95rem 27rem;
  position: relative;

  @media only screen and (max-width: 767px) {
    margin: 0rem 24rem 24rem; /* tighter on mobile for table space */
  }
`;

const Title = styled.div`
  font-size: 32.5rem;
  font-weight: bold;
  line-height: 0.92;
  text-align: center;
  color: #fff;
  margin-top: 20rem;
  margin-bottom: 18rem;

  @media (max-width: 767px) {
    font-size: 58rem; /* a bit smaller than UnderConstruction to fit controls */
    margin-bottom: 24rem;
  }
`;

/** Full-width sponsor strip centered */
const SponsorBar = styled.div`
  width: 100%;
  margin: 0 auto 18rem auto;
  padding: 10px 16px;
  max-width: 1100px;

  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;

  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: center;
  gap: 12px;

  img {
    height: 22px;
    width: auto;
    display: block;
    object-fit: contain;
  }

  span {
    color: #ffffffd9;
    font-size: 14px;
    letter-spacing: 0.3px;
  }
`;

const Controls = styled.div`
  max-width: 1100px;
  margin: 0 auto 16px auto;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const TableShell = styled.div`
  max-width: 1100px;
  margin: 0 auto;

  /* Make AntD table fit the dark theme */
  .ant-table,
  .ant-table-container,
  .ant-table-content,
  .ant-table-wrapper,
  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    background: transparent !important;
  }

  .ant-table-thead > tr > th {
    color: #ffffffcc;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    font-weight: 600;
  }

  .ant-table-tbody > tr > td {
    color: #fff;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .ant-table-tbody > tr:hover > td {
    background: rgba(255, 255, 255, 0.05) !important;
  }

  .ant-pagination-item a,
  .ant-pagination-prev .ant-pagination-item-link,
  .ant-pagination-next .ant-pagination-item-link {
    color: #fff;
  }
  .ant-pagination-item-active a {
    color: #df1e33;
  }
  .ant-pagination-item-active {
    border-color: #df1e33;
  }
`;

const BubbleLayer = styled.div`
  /* decorative layers; same pattern as UnderConstruction */
  position: relative;
  pointer-events: none;

  @media screen and (max-width: 767px) {
    display: none;
  }

  .left {
    position: absolute;
    left: -20rem;
    top: -110rem;
    width: 384rem;
    height: 720rem;
    opacity: 0.6;
  }

  .right {
    position: absolute;
    top: 75rem;
    right: -30rem;
    width: 535.5rem;
    opacity: 0.6;
  }
`;

export default function LeaderboardsPage() {
  // -----------------------------
  // State
  // -----------------------------
  const [rawData, setRawData] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState('');

  // -----------------------------
  // Fetch
  // -----------------------------
  const fetchData = useCallback(async (signal) => {
    setLoading(true);
    setErr(null);
    try {
      // Fetch leaderboard summary (active tournament)
      const res = await fetch(ENDPOINT, { signal /* mode:'cors' by default */ });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setRawData(json);
    } catch (e) {
      setErr(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  // -----------------------------
  // Transform -> array of rows
  // -----------------------------
  const rows = useMemo(() => {
    const arr = Object.entries(rawData || {}).map(([username, stats]) => ({
      username,
      ...stats,
    }));
    arr.sort((a, b) => (b?.totalKills || 0) - (a?.totalKills || 0));
    return arr.map((r, idx) => ({ ...r, rank: idx + 1 }));
  }, [rawData]);

  // -----------------------------
  // Filtering (search by username, case-insensitive)
  // -----------------------------
  const filteredRows = useMemo(() => {
    if (!query) return rows;
    const q = query.trim().toLowerCase();
    return rows.filter((r) => r.username?.toLowerCase().includes(q));
  }, [rows, query]);

  // -----------------------------
  // Columns (based on provided data)
  // -----------------------------
  const columns = useMemo(
    () => [
      { title: 'Rank', dataIndex: 'rank', key: 'rank', width: 90, sorter: (a, b) => a.rank - b.rank, defaultSortOrder: 'ascend' },
      { title: 'Player', dataIndex: 'username', key: 'username', ellipsis: true },
      { title: 'Matches', dataIndex: 'matchesPlayed', key: 'matchesPlayed', width: 120, sorter: (a, b) => (a.matchesPlayed || 0) - (b.matchesPlayed || 0), render: v => v ?? 0 },
      { title: 'Total Kills', dataIndex: 'totalKills', key: 'totalKills', width: 130, sorter: (a, b) => (a.totalKills || 0) - (b.totalKills || 0), render: v => v ?? 0 },
      { title: 'Total Headshots', dataIndex: 'totalHeadshots', key: 'totalHeadshots', width: 160, sorter: (a, b) => (a.totalHeadshots || 0) - (b.totalHeadshots || 0), render: v => v ?? 0 },
      { title: 'Headshot Ratio (%)', dataIndex: 'headshotRatio', key: 'headshotRatio', width: 170, sorter: (a, b) => (a.headshotRatio || 0) - (b.headshotRatio || 0), render: v => v ?? 0 },
    ],
    []
  );

  const renderBody = () => {
    if (loading) {
      return (
        <div>
          <Skeleton active paragraph={{ rows: 1 }} />
          <Skeleton active paragraph={{ rows: 1 }} />
          <Skeleton active paragraph={{ rows: 1 }} />
          <Skeleton active paragraph={{ rows: 1 }} />
        </div>
      );
    }
    if (err) {
      return (
        <Result
          status="error"
          title="Failed to load leaderboards"
          subTitle={String(err?.message || 'Unknown error')}
          extra={<Button icon={<ReloadOutlined />} onClick={() => fetchData()}>Retry</Button>}
        />
      );
    }
    if (!filteredRows?.length) {
      return <Result status="info" title="No records found" subTitle="Try adjusting your search." />;
    }
    return (
      <Table
        rowKey={(r) => r.username}
        columns={columns}
        dataSource={filteredRows}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        scroll={{ x: 720 }}
      />
    );
  };

  return (
    <Wrapper>
      {/* Keep the same site header */}
      <Header isStaking={true} />

      <Helmet>
        <title>Undead Blocks — Leaderboards</title>
        <meta name="description" content="Explore the Undead Blocks leaderboards with real-time standings for the active tournament." />
        <meta property="og:title" content="Undead Blocks — Leaderboards" />
        <meta property="og:description" content="Explore the Undead Blocks leaderboards with real-time standings for the active tournament." />
      </Helmet>

      {/* Decorative side layers (optional; same as UnderConstruction) */}
      <BubbleLayer aria-hidden="true">
        <img className="left" src={leftLayer} alt="" />
        <img className="right" src={rightLayer} alt="" />
      </BubbleLayer>

      <Info>
        <Title>Leaderboards</Title>

        <SponsorBar>
          <span>Sponsored by Avalanche</span>
          <img src={avalancheLogo} alt=" Sponsor" />
        </SponsorBar>

        <Controls>
          <Input
            allowClear
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by username"
            prefix={<SearchOutlined />}
            aria-label="Search by username"
          />
          <Button icon={<ReloadOutlined />} onClick={() => fetchData()} aria-label="Reload">
            Refresh
          </Button>
        </Controls>

        <TableShell>{renderBody()}</TableShell>
      </Info>
    </Wrapper>
  );
}



